import React, { useContext } from "react";
import { PropsWithChildren } from "react";
import { Alert, Box, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { OnboardingContext } from "../../../../store/OnboardingContext";

/**
 * PM5 T2 (money path, batch "onboarding") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `sx`.
 * See docs/mui-conversion-guide.md.
 *
 * **SUBSCRIPTIONS ESCALATION CLASS.** Layout shell shared by every signup
 * step, including the error banner the funnel shows when the API rejects a
 * giftcard code / phone number / email. Styling-only: the error is read from
 * the same context field, rendered by the same component, dismissed by the
 * same `resetError()` call.
 *
 * `Alert` moves from `@material-ui/lab` to `@mui/material` core — it was
 * promoted in the v5 line and `@mui/lab` is not installed (guide §1.4).
 */

// was: useStyles().parent
const parentSx: SxProps<Theme> = { margin: "0 12px" };

// was: useStyles().errorAlert
const errorAlertSx: SxProps<Theme> = (theme) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
});

// was: useStyles().formSubHeader
const formSubHeaderSx: SxProps<Theme> = (theme) => ({
  fontSize: "1.6rem",
  fontWeight: 500,
  marginTop: "24px",
  marginBottom: "12px",
  color: "#eee",
  textAlign: "center",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.8rem",
    lineHeight: "1.8rem",
    marginBottom: theme.spacing(2),
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.8rem",
    lineHeight: "1.8rem",
  },
});

const StepContainer = ({ children }: PropsWithChildren<{}>) => {
  return <Box sx={parentSx}>{children}</Box>;
};

const Title = ({ title }: { title: string }) => {
  return (
    <Typography variant="h2" sx={formSubHeaderSx}>
      {title}
    </Typography>
  );
};

const Body = ({ children }: PropsWithChildren<{}>) => {
  const { error, resetError } = useContext(OnboardingContext);

  return (
    <Box>
      {error && (
        <>
          <Alert
            severity="error"
            variant="filled"
            sx={errorAlertSx}
            onClose={() => resetError()}
          >
            {error}
          </Alert>
        </>
      )}
      {children}
    </Box>
  );
};

const Actions = ({ children }: PropsWithChildren<{}>) => <Box>{children}</Box>;

StepContainer.Title = Title;
StepContainer.Body = Body;
StepContainer.Actions = Actions;
export default StepContainer;
